import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import NoShowCountdown from '../../components/LeadsVo/NoShowCountdown';
import RdvStatusBadge from '../../components/LeadsVo/RdvStatusBadge';
import {
  useCancelRdv,
  useLeadsVoAgent,
  useLeadsVoRdvs,
  useReportNoShow,
  useRescheduleRdv,
} from '../../hooks/useLeadsVoQueries';
import {
  canCancelRdv,
  canReportNoShow,
  canRescheduleRdv,
  describeRdvActionError,
  rdvStartDate,
} from '../../utils/leadsVoRdvActions';

const headerStyle = {
  background: '#262626',
  color: 'white',
  fontWeight: 'bold',
  fontSize: 14,
  position: 'sticky' as const,
  top: 0,
  zIndex: 100,
  whiteSpace: 'nowrap' as const,
};

const cellStyle = {
  height: 55,
  verticalAlign: 'middle' as const,
  fontSize: 14,
  color: '#262626',
};

// L'URL d'annonce vient d'un service externe : on ne rend le lien que pour
// les schémas http(s) (bloque les URI javascript: et autres).
const safeHttpUrl = (url: string | undefined): string | undefined =>
  url && /^https?:\/\//i.test(url) ? url : undefined;

// Valeur d'un <input type="datetime-local"> (heure locale, sans fuseau).
const toDatetimeLocalValue = (date: Date | null): string => {
  if (!date) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const Rdvs = () => {
  // Tous les hooks DOIVENT être appelés inconditionnellement (Rules of Hooks).
  // Le early-return Navigate vient ensuite, après tous les useXxx.
  const { data: agentData, isLoading: agentLoading } = useLeadsVoAgent();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data, isLoading } = useLeadsVoRdvs(page + 1, rowsPerPage);
  const reportNoShow = useReportNoShow();
  const cancelRdv = useCancelRdv();
  const rescheduleRdv = useRescheduleRdv();

  const [noShowDialog, setNoShowDialog] = useState<any>(null);
  const [noShowMotif, setNoShowMotif] = useState('');
  const [cancelDialog, setCancelDialog] = useState<any>(null);
  const [cancelMotif, setCancelMotif] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [rescheduleDialog, setRescheduleDialog] = useState<any>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleMotif, setRescheduleMotif] = useState('');
  const [rescheduleError, setRescheduleError] = useState('');

  if (!agentLoading && agentData?.partner?.statut !== 'active') {
    return <Navigate to="/leads-vo/onboarding" replace />;
  }

  const handleNoShow = async () => {
    if (!noShowDialog) return;
    await reportNoShow.mutateAsync({ rdvId: noShowDialog.id, motif: noShowMotif });
    setNoShowDialog(null);
    setNoShowMotif('');
  };

  // Le partenaire peut refuser (400, créneau écoulé…) : on affiche son motif dans la
  // fenêtre au lieu de la laisser ouverte sans explication.
  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      setCancelError('');
      await cancelRdv.mutateAsync({ rdvId: cancelDialog.id, motif: cancelMotif });
      setCancelDialog(null);
      setCancelMotif('');
    } catch (error) {
      setCancelError(describeRdvActionError(error));
    }
  };

  const canCancel = (rdv: any) => canCancelRdv(rdv);

  const openReschedule = (rdv: any) => {
    const isPostponed = (rdv.statut ?? rdv.status) === 'postponed';
    setRescheduleDate(isPostponed ? '' : toDatetimeLocalValue(rdvStartDate(rdv)));
    setRescheduleMotif('');
    setRescheduleError('');
    setRescheduleDialog(rdv);
  };

  // Avec une date : nouveau créneau, RDV toujours confirmé. Sans : RDV « décalé »,
  // la date sera fixée plus tard. Le partenaire revérifie le créneau (agenda, trajet).
  const handleReschedule = async (withDate: boolean) => {
    if (!rescheduleDialog) return;
    try {
      setRescheduleError('');
      await rescheduleRdv.mutateAsync({
        rdvId: rescheduleDialog.id,
        newSlotStart: withDate ? new Date(rescheduleDate).toISOString() : undefined,
        motif: rescheduleMotif.trim() || undefined,
      });
      setRescheduleDialog(null);
    } catch (error) {
      setRescheduleError(describeRdvActionError(error));
    }
  };

  const isRescheduleDialogPostponed = (rescheduleDialog?.statut ?? rescheduleDialog?.status) === 'postponed';
  const rescheduleStart = rescheduleDate ? new Date(rescheduleDate) : null;
  const isRescheduleDateValid =
    !!rescheduleStart && !Number.isNaN(rescheduleStart.getTime()) && rescheduleStart.getTime() > Date.now();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, px: 4 }}>
      <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626', mb: 3 }}>
        Mes Rendez-vous
      </Typography>

      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle}>Date</TableCell>
                <TableCell sx={headerStyle}>Lead</TableCell>
                <TableCell sx={headerStyle}>Véhicule</TableCell>
                <TableCell sx={headerStyle}>Téléphone</TableCell>
                <TableCell sx={headerStyle}>Statut</TableCell>
                <TableCell sx={headerStyle}>Délai contestation</TableCell>
                <TableCell sx={headerStyle} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.rdvs && data.rdvs.length > 0 ? (
                data.rdvs.map((rdv: any) => {
                  const nom = rdv.particulier?.nom ?? rdv.lead_name ?? '—';
                  const tel = rdv.particulier?.telephone ?? rdv.particulier?.phone ?? rdv.lead_phone ?? '—';
                  const v = rdv.vehicule;
                  const vehicule = v ? `${v.marque} ${v.modele} ${v.annee}` : rdv.lead_vehicle ?? '—';
                  const date = rdv.slot?.start ?? rdv.slot_start ?? rdv.scheduled_at;
                  const statut = rdv.statut ?? rdv.status;
                  const adUrl = safeHttpUrl(rdv.ad_url ?? rdv.lead_source_url);

                  return (
                  <TableRow key={rdv.id} hover sx={{ '&:hover': { backgroundColor: '#FAFAFA' } }}>
                    <TableCell sx={cellStyle}>
                      {statut === 'postponed'
                        ? 'Date à fixer'
                        : date
                          ? new Date(date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      {rdv.reschedule_count > 0 && (
                        <Typography sx={{ fontSize: 12, color: '#757575' }}>
                          Décalé {rdv.reschedule_count} fois
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>{nom}</TableCell>
                    <TableCell sx={cellStyle}>
                      {adUrl ? (
                        <a
                          href={adUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#262626', textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >
                          {vehicule} ↗
                        </a>
                      ) : (
                        vehicule
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle}>{tel}</TableCell>
                    <TableCell sx={cellStyle}>
                      <RdvStatusBadge status={statut} />
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {rdv.status === 'completed' && rdv.locked_at && (
                        <NoShowCountdown lockedAt={rdv.locked_at} />
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle} align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        {canRescheduleRdv(rdv) && (
                          <Button
                            size="small"
                            onClick={() => openReschedule(rdv)}
                            sx={{
                              backgroundColor: '#F3E5F5',
                              color: '#6A1B9A',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 13,
                              '&:hover': { backgroundColor: '#E1BEE7' },
                            }}
                          >
                            {statut === 'postponed' ? 'Fixer la date' : 'Décaler'}
                          </Button>
                        )}
                        {canCancel(rdv) && (
                          <Button
                            size="small"
                            onClick={() => {
                              setCancelError('');
                              setCancelDialog(rdv);
                            }}
                            sx={{
                              backgroundColor: '#FFF3E0',
                              color: '#E65100',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 13,
                              '&:hover': { backgroundColor: '#FFE0B2' },
                            }}
                          >
                            Annuler
                          </Button>
                        )}
                        {canReportNoShow(rdv) && (
                          <Button
                            size="small"
                            onClick={() => setNoShowDialog(rdv)}
                            sx={{
                              backgroundColor: '#FFEBEE',
                              color: '#C62828',
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: 13,
                              '&:hover': { backgroundColor: '#FFCDD2' },
                            }}
                          >
                            No-show
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#464646' }}>
                    Aucun rendez-vous pour le moment
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.totalItems ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Lignes par page"
          sx={{ borderTop: '1px solid #E0E0E0' }}
        />
      </Paper>

      <Dialog
        open={!!noShowDialog}
        onClose={() => setNoShowDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>Signaler un no-show</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: '#262626' }}>
              <strong>Lead :</strong> {noShowDialog?.particulier?.nom ?? noShowDialog?.lead_name ?? '—'} — {noShowDialog?.vehicule ? `${noShowDialog.vehicule.marque} ${noShowDialog.vehicule.modele}` : noShowDialog?.lead_vehicle ?? '—'}
            </Typography>
          </Box>
          <TextField
            label="Motif du no-show"
            value={noShowMotif}
            onChange={(e) => setNoShowMotif(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNoShowDialog(null)} sx={{ color: '#464646', textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            onClick={handleNoShow}
            disabled={!noShowMotif.trim() || reportNoShow.isPending}
            sx={{
              backgroundColor: '#C62828',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#B71C1C' },
            }}
          >
            {reportNoShow.isPending ? 'Envoi...' : 'Confirmer le no-show'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!cancelDialog}
        onClose={() => setCancelDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>Annuler le rendez-vous</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: '#262626' }}>
              <strong>Lead :</strong> {cancelDialog?.particulier?.nom ?? '—'} — {cancelDialog?.vehicule ? `${cancelDialog.vehicule.marque} ${cancelDialog.vehicule.modele}` : '—'}
            </Typography>
          </Box>
          <TextField
            label="Motif de l'annulation"
            value={cancelMotif}
            onChange={(e) => setCancelMotif(e.target.value)}
            multiline
            rows={3}
            fullWidth
            required
            helperText="10 caractères minimum"
            error={cancelMotif.length > 0 && cancelMotif.trim().length < 10}
          />
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialog(null)} sx={{ color: '#464646', textTransform: 'none' }}>
            Retour
          </Button>
          <Button
            onClick={handleCancel}
            disabled={cancelMotif.trim().length < 10 || cancelRdv.isPending}
            sx={{
              backgroundColor: '#E65100',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#BF360C' },
            }}
          >
            {cancelRdv.isPending ? 'Annulation...' : "Confirmer l'annulation"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rescheduleDialog}
        onClose={() => setRescheduleDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#262626' }}>
          {isRescheduleDialogPostponed ? 'Fixer la nouvelle date' : 'Décaler le rendez-vous'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: '#262626' }}>
              <strong>Lead :</strong> {rescheduleDialog?.particulier?.nom ?? rescheduleDialog?.lead_name ?? '—'} —{' '}
              {rescheduleDialog?.vehicule
                ? `${rescheduleDialog.vehicule.marque} ${rescheduleDialog.vehicule.modele}`
                : rescheduleDialog?.lead_vehicle ?? '—'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: '#464646', mb: 2 }}>
            Le rendez-vous reste le même (même pack, pas de nouvelle facturation). Le vendeur reçoit ses rappels pour
            la nouvelle date et votre agenda est mis à jour.
          </Typography>
          <TextField
            label="Nouvelle date et heure"
            type="datetime-local"
            value={rescheduleDate}
            onChange={(e) => setRescheduleDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            error={!!rescheduleDate && !isRescheduleDateValid}
            helperText={rescheduleDate && !isRescheduleDateValid ? 'Choisissez une date à venir' : ' '}
          />
          <TextField
            label="Motif (optionnel)"
            value={rescheduleMotif}
            onChange={(e) => setRescheduleMotif(e.target.value)}
            multiline
            rows={2}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
          {rescheduleError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {rescheduleError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setRescheduleDialog(null)} sx={{ color: '#464646', textTransform: 'none' }}>
            Retour
          </Button>
          {!isRescheduleDialogPostponed && (
            <Button
              onClick={() => handleReschedule(false)}
              disabled={rescheduleRdv.isPending}
              sx={{ color: '#6A1B9A', textTransform: 'none', fontWeight: 600 }}
            >
              Date pas encore fixée
            </Button>
          )}
          <Button
            onClick={() => handleReschedule(true)}
            disabled={!isRescheduleDateValid || rescheduleRdv.isPending}
            sx={{
              backgroundColor: '#6A1B9A',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#4A148C' },
              '&.Mui-disabled': { backgroundColor: '#E0E0E0' },
            }}
          >
            {rescheduleRdv.isPending ? 'Envoi...' : 'Enregistrer la nouvelle date'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Rdvs;
